import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import styles from '@components/CareProfile.module.css';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Form from '@components/forms/Form';
import {
  CareProfileFragment as CareProfile,
  CareProfileFinancialManagerEnum,
  CareProfileHealthInsuranceTypeEnum,
  CareProfileInsuranceTypeEnum,
  CareProfileVeteranConnectionEnum,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForBoolean, optionsForVeteranConnection } from '@utils/optionUtils';

type FormData = {
  financialManager: CareProfileFinancialManagerEnum | null;
  financialManagerOther: string | null;
  healthInsuranceTypes: CareProfileHealthInsuranceTypeEnum[];
  healthInsuranceTypesOther: string | null;
  insuranceTypes: CareProfileInsuranceTypeEnum[];
  insuranceTypesOther: string | null;
  vaConnected: boolean | null;
  veteranConnection: CareProfileVeteranConnectionEnum[];
};

type Props = {
  careProfile: CareProfile;
  consultId: string | undefined | null;
  initialConsultReportId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultFinancialForm = ({
  careProfile,
  consultId,
  initialConsultReportId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  const initialValues = {
    financialManager: careProfile?.financialManager?.financialManagerOption ?? null,
    financialManagerOther: careProfile?.financialManager?.other ?? null,
    healthInsuranceTypes: careProfile.healthInsuranceTypes?.healthInsuranceTypesOptions ?? [],
    healthInsuranceTypesOther: careProfile.healthInsuranceTypes?.other ?? null,
    insuranceTypes: careProfile?.insuranceTypes?.insuranceTypesOptions ?? [],
    insuranceTypesOther: careProfile.insuranceTypes?.other ?? null,
    vaConnected: careProfile.vaConnected ?? null,
    veteranConnection: careProfile.veteranConnection ?? []
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      financialManager: yup.string().nullable().defined().oneOf(Object.values(CareProfileFinancialManagerEnum)),
      financialManagerOther: yup.string().nullable().defined(),
      healthInsuranceTypes: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileHealthInsuranceTypeEnum)).defined()),
      healthInsuranceTypesOther: yup.string().nullable().defined(),
      insuranceTypes: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileInsuranceTypeEnum)).defined()),
      insuranceTypesOther: yup.string().nullable().defined(),
      vaConnected: yup.boolean().nullable().defined(),
      veteranConnection: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileVeteranConnectionEnum)).defined())
    })
    .required();

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('social');
      } else {
        onSuccess('legal');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const healthInsuranceTypes = form.watch('healthInsuranceTypes');
  const insuranceTypes = form.watch('insuranceTypes');
  const financialManager = form.watch('financialManager');

  const vaConnected = form.watch('vaConnected');

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        financialManager: {
          financialManagerOption: form.getValues().financialManager,
          other:
            financialManager === CareProfileFinancialManagerEnum.other ? form.getValues().financialManagerOther : null
        },
        healthInsuranceTypes: {
          healthInsuranceTypesOptions: form.getValues().healthInsuranceTypes,
          other: healthInsuranceTypes.includes(CareProfileHealthInsuranceTypeEnum.other)
            ? form.getValues().healthInsuranceTypesOther
            : null
        },
        id: initialConsultReportId,
        insuranceTypes: {
          insuranceTypesOptions: form.getValues().insuranceTypes,
          other: insuranceTypes.includes(CareProfileInsuranceTypeEnum.other)
            ? form.getValues().insuranceTypesOther
            : null
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (!questions) {
            setSubmitted(true);
          } else {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [
    consultId,
    financialManager,
    form,
    healthInsuranceTypes,
    initialConsultReportId,
    insuranceTypes,
    intl,
    onSuccess,
    questions,
    updateInitialConsultReport
  ]);

  const financialManagerOptions = useMemo(() => {
    const options = [
      CareProfileFinancialManagerEnum.loved_one,
      CareProfileFinancialManagerEnum.other,
      CareProfileFinancialManagerEnum.fiduciary,
      CareProfileFinancialManagerEnum.unsure
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.financial_manager.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const insuranceTypeOptions = useMemo(() => {
    const options = [
      CareProfileInsuranceTypeEnum.life_insurance,
      CareProfileInsuranceTypeEnum.long_term_care_insurance,
      CareProfileInsuranceTypeEnum.other,
      CareProfileInsuranceTypeEnum.none
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.insurance_types.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const healthInsuranceTypeOptions = useMemo(() => {
    const options = [
      CareProfileHealthInsuranceTypeEnum.medicare,
      CareProfileHealthInsuranceTypeEnum.supplemental,
      CareProfileHealthInsuranceTypeEnum.medicaid,
      CareProfileHealthInsuranceTypeEnum.private,
      CareProfileHealthInsuranceTypeEnum.veteran,
      CareProfileHealthInsuranceTypeEnum.other,
      CareProfileHealthInsuranceTypeEnum.none
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.health_insurance_types.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const veteranConnectionOptions = useMemo(() => optionsForVeteranConnection(intl), [intl]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.HEALTH_INSURANCE_TYPES) ? undefined : 'ion-hide'
        }
      >
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.healthInsuranceTypes}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.health_insurance_types.content' })}
          name="healthInsuranceTypes"
          options={healthInsuranceTypeOptions}
        />
        <div
          className={healthInsuranceTypes.includes(CareProfileHealthInsuranceTypeEnum.other) ? undefined : 'ion-hide'}
        >
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.healthInsuranceTypesOther}
            label={intl.formatMessage({ id: 'dictionary.other' })}
            name="healthInsuranceTypesOther"
            spellcheck
          />
        </div>
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.VA_CONNECTED) ? undefined : 'ion-hide'}>
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.vaConnected}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.va_connected.content' })}
          name="vaConnected"
          options={booleanOptions}
        />
      </div>
      <div className={vaConnected ? undefined : 'ion-hide'}>
        <div
          className={
            !questions || questions?.includes(InitialConsultFields.VETERAN_CONNECTION) ? undefined : 'ion-hide'
          }
        >
          <CheckboxGroup<FormData>
            control={form.control}
            errors={form.formState.errors.veteranConnection}
            label={intl.formatMessage({ id: 'models.initialConsultReport.questions.veteran_connection.content' })}
            name="veteranConnection"
            options={veteranConnectionOptions}
          />
        </div>
      </div>
      <div
        className={!questions || questions?.includes(InitialConsultFields.FINANCIAL_MANAGER) ? undefined : 'ion-hide'}
      >
        <RadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.financialManager}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.financial_manager.content' })}
          name="financialManager"
          options={financialManagerOptions}
        />
        <div className={financialManager === CareProfileFinancialManagerEnum.other ? undefined : 'ion-hide'}>
          <Input
            control={form.control}
            error={form.formState.errors.financialManagerOther}
            label={intl.formatMessage({ id: 'dictionary.other' })}
            name="financialManagerOther"
            spellcheck
          />
        </div>
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.INSURANCE_TYPES) ? undefined : 'ion-hide'}>
        <CheckboxGroup
          control={form.control}
          errors={form.formState.errors.insuranceTypes}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.insurance_types.content' })}
          name="insuranceTypes"
          options={insuranceTypeOptions}
        />
        <div className={insuranceTypes.includes(CareProfileInsuranceTypeEnum.other) ? undefined : 'ion-hide'}>
          <Input
            control={form.control}
            error={form.formState.errors.insuranceTypesOther}
            label={intl.formatMessage({ id: 'dictionary.other' })}
            name="insuranceTypesOther"
            spellcheck
          />
        </div>
      </div>
      {!questions ? (
        <div className={styles.spaceBetween}>
          <SecondaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(false)}
            textKey="dictionary.previous"
            type="submit"
          />
          <PrimaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(true)}
            textKey="dictionary.next"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default InitialConsultFinancialForm;
